import { translate, type TranslateFn } from "@/lib/translation";
import { getIntlLocale, formatFixed, currencyDisplayFor } from "@/lib/locale";

// TECH-DEBT: dead export — no production consumers; only imported by portfolioUtils.test.ts.
// Production code uses useFormatters().formatCurrency (which also handles privacy mode).
// Consider removing this export and migrating the test to useFormatters.
export const formatCurrency = (
  value: number,
  currency: string | null | undefined,
) => {
  if (!currency) return "-";
  return new Intl.NumberFormat(getIntlLocale("en-US"), {
    style: "currency",
    currency: currency,
    currencyDisplay: currencyDisplayFor(getIntlLocale("en-US"), currency),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatPercent = (value: number) => {
  const fixedValue = Math.abs(value) === 0 ? 0 : value;
  return `${fixedValue >= 0 ? "+" : ""}${formatFixed(fixedValue, 2)}%`;
};

export const parseNumber = (value: unknown): number => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

export const formatSyncDate = (isoString: string): string => {
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

export const getGainLossColor = (value: number) =>
  value > 0 ? "text-gain" : value < 0 ? "text-loss" : "text-muted-foreground";

export const brokerageNameMap: { [key: string]: string } = {
  t212: "Trading 212",
  degiro: "DEGIRO",
  ib: "Interactive Brokers",
  revolut: "Revolut",
  xtb: "XTB",
  robinhood: "Robinhood Europe (Beta)",
  // Read on access, so the label follows the current language.
  get manual() {
    return translate("portfolios:brokerages.manual");
  },
};

export interface CombinedMetrics {
  totalValue: number;
  totalGainLoss: number;
  holdingsValue: number;
  holdingsGainLoss: number;
  holdingsCostBase: number;
  totalCostBase: number;
  /** Part of totalCostBase that came from broker rewards rather than the user's own money. */
  totalRewards: number;
}

// Minimal Portfolio interface for calculation purposes
interface CalculationPortfolio {
  total_value: number;
  invested_capital: number;
  total_gain_loss: number;
  holdings_value: number;
  holdings_gain_loss: number;
  cash_total?: number | null;
  reward_base_total?: number | null;
}

export const calculateCombinedMetrics = (
  portfolios: CalculationPortfolio[],
): CombinedMetrics => {
  return portfolios.reduce(
    (acc, p) => {
      acc.totalValue += p.total_value;
      acc.totalGainLoss += p.total_gain_loss;
      acc.holdingsValue += p.holdings_value;
      acc.holdingsGainLoss += p.holdings_gain_loss;

      const holdingsCostBase = p.holdings_value - p.holdings_gain_loss;

      acc.holdingsCostBase += holdingsCostBase;
      acc.totalCostBase += p.invested_capital;
      acc.totalRewards += p.reward_base_total ?? 0;
      return acc;
    },
    {
      totalValue: 0,
      totalGainLoss: 0,
      holdingsValue: 0,
      holdingsGainLoss: 0,
      holdingsCostBase: 0,
      totalCostBase: 0,
      totalRewards: 0,
    } as CombinedMetrics,
  );
};

export const calculateTotalReturnPercent = (
  totalGainLoss: number,
  totalCostBase: number,
): number => {
  return totalCostBase > 0 ? (totalGainLoss / totalCostBase) * 100 : 0;
};

// The combined portfolio's name comes from the backend in English ("All Portfolios"),
// so it is shown through its translation; every other name is the user's own.
export const portfolioDisplayName = (
  portfolio: { name: string; is_combined?: boolean },
  t: TranslateFn = translate,
): string =>
  portfolio.is_combined ? t("common:rollupPortfolioName") : portfolio.name;
